import { useMemo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

/**
 * Curvas de nivel generadas (determinísticas): el relieve de la cordillera como
 * textura. Se usan en el riel y en el login. La deriva es lentísima (60–90 s) —
 * se percibe como "vivo" sin distraer, y se apaga con prefers-reduced-motion.
 */
function ring(cx: number, cy: number, r: number, seed: number, wobble: number) {
  const pts: string[] = []
  const steps = 72
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2
    const n =
      Math.sin(a * 3 + seed) * 0.5 +
      Math.sin(a * 5 + seed * 1.7) * 0.28 +
      Math.sin(a * 2 - seed * 0.6) * 0.42
    const rr = r * (1 + wobble * n)
    // Cada anillo se corre un poco: las cotas de un cerro no son concéntricas.
    const x = cx + Math.cos(seed) * r * 0.22 + Math.cos(a) * rr * 1.3
    const y = cy + Math.sin(seed * 1.3) * r * 0.16 + Math.sin(a) * rr
    pts.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`)
  }
  return pts.join(' ') + 'Z'
}

export default function Contours({
  color = 'rgba(143, 209, 173, 0.16)',
  peaks = [[180, 260], [620, 120], [420, 620]],
  rings = 9,
  strokeWidth = 1,
  drift = true,
}: {
  color?: string
  peaks?: Array<[number, number]>
  rings?: number
  strokeWidth?: number
  drift?: boolean
}) {
  const reduce = useReducedMotion()
  const paths = useMemo(
    () =>
      peaks.flatMap(([cx, cy], p) =>
        Array.from({ length: rings }, (_, k) => ring(cx, cy, 22 + k * 30 + (k % 3) * 4, p * 2.3 + k * 0.29, 0.14 + k * 0.016)),
      ),
    [peaks, rings],
  )

  return (
    <motion.svg
      viewBox="0 0 800 800"
      preserveAspectRatio="xMidYMid slice"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      animate={drift && !reduce ? { x: [0, -18, 0], y: [0, 12, 0], rotate: [0, 1.2, 0] } : undefined}
      transition={{ duration: 75, repeat: Infinity, ease: 'easeInOut' }}
      aria-hidden
    >
      {paths.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={color} strokeWidth={i % rings === rings - 4 ? strokeWidth * 1.8 : strokeWidth} />
      ))}
    </motion.svg>
  )
}
